import { cn } from '@/lib/cn';
import { getForm, levelOf } from '@/lib/cardForm';
import type { CRPlayerCard } from '@/types/qbot';
import { CardIcon } from './CardIcon';

interface DeckGridProps {
  cards: CRPlayerCard[];
  /** Show the in-game level overlay */
  showLevels?: boolean;
  /** Optional avg elixir badge shown in the corner */
  avgElixir?: number;
  /** Makes each card tappable — used to filter the meta by a card you own. */
  onCardClick?: (card: CRPlayerCard) => void;
  className?: string;
}

/**
 * 4x2 grid of card icons — the canonical Clash Royale deck shape.
 *
 * Mirrors the layout that QBot's `deckImage.ts` generates server-side for
 * Discord/Telegram bot embeds, but rendered natively so we get sharp
 * scaling, no PNG download, and instant updates when the deck changes.
 */
export function DeckGrid({
  cards,
  showLevels = true,
  avgElixir,
  onCardClick,
  className,
}: DeckGridProps) {
  return (
    <div className={cn('relative grid grid-cols-4 gap-1.5', className)}>
      {cards.slice(0, 8).map((c, i) => {
        const form = getForm(c);
        const iconUrl =
          form === 'hero'
            ? c.iconUrls?.heroMedium
            : form === 'evolution'
              ? c.iconUrls?.evolutionMedium
              : c.iconUrls?.medium;
        return (
          <CardIcon
            key={`${c.id}-${i}`}
            id={c.id}
            name={c.name}
            form={form}
            iconUrl={iconUrl}
            level={showLevels ? levelOf(c) : undefined}
            onClick={onCardClick ? () => onCardClick(c) : undefined}
          />
        );
      })}
      {typeof avgElixir === 'number' && (
        <div className="absolute -right-1 -top-2 flex h-6 items-center gap-1 rounded-full bg-fuchsia-600 px-2 text-[11px] font-bold text-white shadow">
          <span aria-hidden>⚡</span>
          <span className="tabular-nums">{avgElixir.toFixed(1)}</span>
        </div>
      )}
    </div>
  );
}
